import {
  Paper,
  Table,
  Tooltip,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TableContainer,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { RHFNumberText } from '@/components/forms/hook-form/rhf-number-field';
import { MilestoneStatusLabel } from '@/components/core/milestone-status-label';
import { ChoicesStatusMilestone } from '@/types';
import { CancellationMilestoneDto } from '@/services/cancellation.service';
import { isMilestoneDisabled } from '../../utils/cancel-order.utils';

interface CancellableMilestonesTableProps {
  milestones: CancellationMilestoneDto[];
  selectedMilestones: string[];
  onMilestoneSelect: (milestoneId: string, selected: boolean) => void;
}

export const CancellableMilestonesTable = ({
  milestones,
  selectedMilestones,
  onMilestoneSelect,
}: CancellableMilestonesTableProps) => {
  const { t } = useTranslation();

  const getDisabledTooltip = () =>
    t('This billing milestone was used in another cancellation order');

  if (!milestones || milestones.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('No cancellable milestones available')}</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table
        sx={{
          '& .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: 'background.paper',
          },
          '& .MuiTableRow-root:nth-of-type(odd)': {
            backgroundColor: 'background.paper',
          },
          '& .MuiTableCell-root': {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '30px',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
          },
          '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('Milestone')}</TableCell>
            <TableCell>{t('Date')}</TableCell>
            <TableCell>{t('Amount')}</TableCell>
            <TableCell>{t('State')}</TableCell>
            <TableCell>{t('NIIF 15')}</TableCell>
            <TableCell>{t('Select')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {milestones.map((milestone) => (
            <TableRow key={milestone._id}>
              <TableCell>{`${t('Billing Milestone ')} ${milestone.period}`}</TableCell>
              <TableCell
                sx={{
                  color: 'text.secondary',
                  lineHeight: '24px !important',
                  fontWeight: '400 !important',
                  fontSize: '14px !important',
                }}
              >
                {moment(milestone.date).utc().format('L')}
              </TableCell>
              <TableCell>
                <RHFNumberText
                  value={milestone.amount}
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="$"
                />
              </TableCell>
              <TableCell>
                <MilestoneStatusLabel status={milestone.status as ChoicesStatusMilestone} />
              </TableCell>
              <TableCell>
                <Typography
                  sx={{
                    color: milestone.isNiif15 ? 'success.main' : 'text.secondary',
                    fontSize: '15px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '24px',
                  }}
                >
                  {milestone.isNiif15 ? t('Yes') : t('No')}
                </Typography>
              </TableCell>
              <TableCell>
                {isMilestoneDisabled(milestone.status) ? (
                  <Tooltip title={getDisabledTooltip()} placement="top">
                    <span>
                      <Checkbox
                        disabled
                        checked={false}
                        sx={{
                          color: 'action.disabled',
                          '&.Mui-disabled': {
                            color: 'action.disabled',
                          },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Checkbox
                    checked={selectedMilestones.includes(milestone._id)}
                    onChange={(e) => {
                      onMilestoneSelect(milestone._id, e.target.checked);
                    }}
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                      '&.MuiCheckbox-root': {
                        color: 'primary.main',
                      },
                      '& .MuiSvgIcon-root': {
                        borderColor: 'text.primary',
                      },
                      '&.Mui-checked .MuiSvgIcon-root': {
                        borderColor: 'text.primary',
                      },
                    }}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
